import { Helmet } from 'react-helmet-async';
import { LANGUAGES, type LanguageCode } from '../i18n/config';
import { useI18n } from '../i18n/I18nContext';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  type?: string;
  canonicalPath?: string;
}

const siteUrl = 'https://impresstars.com';

function SEO({ title, description, image = '/impresstars-logo.png', type = 'website', canonicalPath }: SEOProps) {
  const { lang, stripLang } = useI18n();

  const rawPath = canonicalPath ?? stripLang(window.location.pathname);
  const cleanPath = rawPath.split('#')[0].split('?')[0] || '/';

  const canonicalUrl = `${siteUrl}${lang === 'en' ? cleanPath : `/${lang}${cleanPath === '/' ? '' : cleanPath}`}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Impresstars',
    description: description,
    url: siteUrl,
    email: 'info@impresstars.com',
    areaServed: 'CA',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'London',
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
    knowsAbout: [
      'Business Incorporation',
      'AI Solutions',
      'B2B Solutions',
      'Marketing Strategy',
      'Business Software',
      'Brand Design',
      'Business Consulting',
      'Web Development',
    ],
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title} | Impresstars</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />

      {LANGUAGES.map((l) => {
        const href = l.code === 'en'
          ? `${siteUrl}${cleanPath}`
          : `${siteUrl}/${l.code}${cleanPath === '/' ? '' : cleanPath}`;
        return (
          <link key={l.code} rel="alternate" hrefLang={l.code} href={href} />
        );
      })}
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${cleanPath}`} />

      <meta property="og:site_name" content="Impresstars" />
      <meta property="og:locale" content={lang} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image.startsWith('http') ? image : `${siteUrl}${image}`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image.startsWith('http') ? image : `${siteUrl}${image}`} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export default SEO;
